import type {
  HealthSystemDomainLocationsModelsLocationClimateViewModel,
  HealthSystemDomainLocationsModelsTemperatureDayData,
  HealthSystemDomainLocationsModelsTimeSeriesCollectionsData,
  HealthSystemDomainLocationsModelsTimeSeriesData
} from '../api/HealthSystemApi';
import type { ChartPoint, LineSeries } from './time-series';

/**
 * A pen's climate snapshot and rolling series, shown on `LocationClimateViewV2`
 * (`/locations/:id/climate`). Mirrors `GET /locations/climate`, response
 * `HealthSystemDomainLocationsModelsLocationClimateViewModel`. Split into two shapes because the
 * screen replaces them independently: `ClimateReadings` is the current snapshot (gauges, cards),
 * `ClimateSeries` is the trailing window feeding the charts.
 */
export interface ClimateReadings {
  /** Inside temperature, pre-formatted with its unit by the API (e.g. "18.2 °C"). Null when the sensor didn't report. */
  temperatureInside: string | null;
  /** Outside temperature, same pre-formatted-string shape as `temperatureInside`. Null when there was no response at all. */
  temperatureOutside: string | null;
  /** Relative humidity inside the pen, in %. Null when the sensor didn't report. */
  humidityInside: number | null;
  /** Relative humidity outside, in %. Null when there was no response at all, or the response carried none. */
  humidityOutside: number | null;
  /** CO2 concentration, in ppm. Null when the sensor didn't report. */
  co2: number | null;
  /** NH3 (ammonia) concentration, in ppm. Null when the sensor didn't report. */
  nh3: number | null;
  /** Temperature-humidity heat stress index (THI). Null when the sensor didn't report. */
  heatStressIndex: number | null;
  /** Compass direction the wind is coming from (e.g. "NE"). Null when there was no response at all. */
  windDirection: string | null;
  /** Wind direction, in degrees. Null when there was no response at all. */
  windDegree: number | null;
  /** Wind speed, pre-formatted with its unit by the API. Null when there was no response at all. */
  windSpeed: string | null;
  /** '°C' or '°F', the unit `temperatureInside`/`temperatureOutside` are already formatted in. Defaults to '°C' when there was no response at all. */
  temperatureUnit: string;
}

export interface ClimateSeries {
  temperature: LineSeries[];
  co2: LineSeries[];
  nh3: LineSeries[];
  humidity: LineSeries[];
  thi: LineSeries[];
  outsideTemperature: LineSeries[];
}

// Replaced wholesale on every load. Setting each series only when the response carries one is how
// the previous date's curve used to survive a switch to a date that has no readings for it.
export const noSeries: ClimateSeries = {
  temperature: [],
  co2: [],
  nh3: [],
  humidity: [],
  thi: [],
  outsideTemperature: []
};

export const noReadings: ClimateReadings = {
  temperatureInside: null,
  temperatureOutside: null,
  humidityInside: null,
  humidityOutside: null,
  co2: null,
  nh3: null,
  heatStressIndex: null,
  windDirection: null,
  windDegree: null,
  windSpeed: null,
  temperatureUnit: '°C'
};

const toSeries = (
  data: HealthSystemDomainLocationsModelsTimeSeriesData | null | undefined,
  name: string,
  strokeColor: string
): LineSeries[] => {
  if (!data?.data?.length) {
    return [];
  }

  // Points arrive one per hour of the window, already carrying no value for the hours nothing
  // reported. They are passed through as they are: dropping the empty ones closes the gap up.
  const points: ChartPoint[] = data.data.map((point) => ({
    time: point.time,
    value: point.value ?? null,
    min: point.min ?? null,
    max: point.max ?? null
  }));

  return [{ name, strokeColor, data: points }];
};

const toOutsideSeries = (
  days: HealthSystemDomainLocationsModelsTemperatureDayData[] | null | undefined,
  name: string,
  strokeColor: string
): LineSeries[] => {
  if (!days?.length) {
    return [];
  }

  const points: ChartPoint[] = days.flatMap((day) =>
    day.hourData.map((hour) => {
      // The provider formats the value with its unit ("18.2 °C"), which parseFloat stops at.
      const value = Number.parseFloat(hour.temperature);

      return { time: hour.time, value: Number.isFinite(value) ? value : null };
    })
  );

  return points.some((p) => p.value != null) ? [{ name, strokeColor, data: points }] : [];
};

/** Maps the climate snapshot fields of the response to `ClimateReadings`. `data` is the raw `result.data`, which is undefined on a failed or bodiless response. */
export const toClimateReadings = (
  data: HealthSystemDomainLocationsModelsLocationClimateViewModel | undefined
): ClimateReadings => {
  const sensorData = data?.sensorData;

  return {
    temperatureInside: sensorData?.insideTemperature ?? null,
    temperatureOutside: data?.temperature ?? null,
    humidityInside: sensorData?.humidity ?? null,
    humidityOutside: data?.humidity ?? null,
    co2: sensorData?.co2 ?? null,
    nh3: sensorData?.nh3 ?? null,
    heatStressIndex: sensorData?.heatStressIndex ?? null,
    windDirection: data?.windDirection ?? null,
    windDegree: data?.windDegree ?? null,
    windSpeed: data?.windSpeed ?? null,
    temperatureUnit: data?.temperatureUnit === 1 ? '°F' : '°C'
  };
};

/** Maps the response's `timeSeriesData` to the six chart series, keyed off the response's own field names. */
export const toClimateSeries = (
  timeSeries: HealthSystemDomainLocationsModelsTimeSeriesCollectionsData | undefined
): ClimateSeries => ({
  temperature: [
    ...toSeries(timeSeries?.sensorTemperature, 'Inside', '#6366f1'),
    ...toOutsideSeries(timeSeries?.outsideTemperature, 'Outside', '#2ca02c')
  ],
  co2: toSeries(timeSeries?.co2, 'CO2', '#008080'),
  nh3: toSeries(timeSeries?.nh3, 'NH3', '#8b5cf6'),
  humidity: toSeries(timeSeries?.humidity, 'Inside', '#9f1239'),
  thi: toSeries(timeSeries?.heatStressIndex, 'THI', '#f59e0b'),
  outsideTemperature: toOutsideSeries(timeSeries?.outsideTemperature, 'Outside Temp', '#3b82f6')
});
